import React from 'react';
import { Card, CardContent, Box, Typography, Avatar, Chip } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme, color }) => ({
  background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
  border: `1px solid ${color}30`,
  borderRadius: '16px',
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 25px ${color}25`,
    border: `1px solid ${color}50`,
  },
}));

const IconAvatar = styled(Avatar)(({ color }) => ({
  background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
  width: 56,
  height: 56,
  marginBottom: '16px',
  boxShadow: `0 4px 14px ${color}40`,
}));

const TrendChip = styled(Chip)(({ trend }) => ({
  borderRadius: '20px',
  fontSize: '0.75rem',
  fontWeight: 600,
  background: trend >= 0 
    ? 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)' 
    : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
  color: 'white',
  '& .MuiChip-icon': {
    color: 'white',
    fontSize: '16px',
  },
}));

const MetricCard = ({ title, value, icon: Icon, trend, color = '#1976d2', isLoading = false }) => {
  const formatValue = (val) => {
    if (isLoading) return '...';
    if (typeof val === 'number') {
      if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return val.toLocaleString();
    }
    return val;
  };

  const getTrendIcon = () => {
    return trend >= 0 ? <TrendingUp /> : <TrendingDown />;
  };

  return (
    <StyledCard color={color}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <IconAvatar color={color}>
              <Icon sx={{ fontSize: 28, color: 'white' }} />
            </IconAvatar>
            <Typography 
              variant="h3" 
              component="div" 
              sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                mb: 0.5,
                fontSize: isLoading ? '2rem' : '2.5rem',
                lineHeight: 1.2
              }}
            >
              {formatValue(value)}
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ 
                fontWeight: 500,
                fontSize: '0.95rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
        
        {trend !== undefined && trend !== null && !isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <TrendChip
              icon={getTrendIcon()}
              label={`${trend >= 0 ? '+' : ''}${trend}%`}
              size="small"
              trend={trend}
            />
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: '0.75rem', fontWeight: 500 }}
            >
              vs last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default MetricCard; 